import { motion } from "framer-motion";
import { ArrowUpRight, BadgeCheck, Globe2, ShieldCheck, Wallet } from "lucide-react";
import { COURSES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

export default function Courses({ scrollTo }) {
  return (
    <section id="courses" data-testid="courses-section" className="border-y border-brand-line bg-brand-cream/60 py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-8 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="lg:col-span-7"
          >
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-terra">
              Programs · Admissions Open 2026–27
            </p>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight md:text-5xl">
              Six pathways.
              <br />
              One standard of <em className="italic text-brand-terra">excellence</em>.
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            className="self-end text-base leading-relaxed text-brand-muted md:text-lg lg:col-span-5"
          >
            UGC-recognised degrees and AHLEI-certified diplomas — every program
            pairs classroom rigour with paid industrial training, and every fee is
            payable in easy EMIs.
          </motion.p>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {COURSES.map((course, i) => (
            <motion.article
              key={course.title}
              data-testid={`course-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.1 }}
              className="group flex flex-col border border-brand-line bg-brand-surface p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-terra"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm tracking-[0.2em] text-brand-terra">
                  0{i + 1}
                </span>
                <span className="rounded-full border border-brand-line px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-brand-muted">
                  {course.type}
                </span>
              </div>
              <h3 className="mt-6 font-serif text-2xl leading-snug text-brand-ink">
                {course.title}
              </h3>
              {course.highlight && (
                <p
                  data-testid={`course-highlight-${i}`}
                  className="mt-3 flex items-start gap-2 text-sm font-semibold leading-relaxed text-brand-green"
                >
                  <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-terra" />
                  {course.highlight}
                </p>
              )}
              <dl className="mt-7 space-y-3 text-sm">
                {[
                  ["Duration", course.duration],
                  ["Eligibility", course.eligibility],
                  ["Fees", course.fees],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-baseline justify-between gap-4 border-b border-dashed border-brand-line pb-2.5"
                  >
                    <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-muted">
                      {label}
                    </dt>
                    <dd className="text-right font-medium text-brand-ink">{value}</dd>
                  </div>
                ))}
              </dl>
              <p
                data-testid={`course-inclusive-${i}`}
                className="mt-6 flex items-start gap-2.5 rounded-md border border-brand-terra/25 bg-brand-terra/5 px-4 py-3 text-xs font-semibold leading-relaxed text-brand-terra"
              >
                <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0" />
                Fee includes uniform, kitchen kit, service kit & university fees — no hidden charges
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span
                  data-testid={`course-guarantee-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-green/10 px-3.5 py-1.5 text-xs font-semibold text-brand-green"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Written Job Guarantee
                </span>
                <span
                  data-testid={`course-ewyl-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-green/10 px-3.5 py-1.5 text-xs font-semibold text-brand-green"
                >
                  <Wallet className="h-3.5 w-3.5" />
                  Earn While You Learn
                </span>
              </div>
              <button
                data-testid={`course-enquire-${i}`}
                onClick={() => scrollTo("#enquire")}
                className="mt-auto inline-flex items-center gap-1.5 pt-7 text-left text-sm font-semibold text-brand-green transition-colors duration-300 hover:text-brand-terra"
              >
                Enquire about this course
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
